import { useState, useEffect } from "react";
import * as PortOne from "@portone/browser-sdk/v2";

interface PortOneCheckoutProps {
  bookingId: string;
  amount: number;
  orderName: string;
  customerName: string;
  customerEmail?: string;
  onClose: () => void;
}

const STORE_ID = process.env.REACT_APP_PORTONE_STORE_ID || "";
const CHANNEL_KEY_CARD = process.env.REACT_APP_PORTONE_CHANNEL_KEY_CARD || "";

const PortOneCheckout = ({
  bookingId,
  amount,
  orderName,
  customerName,
  customerEmail,
  onClose,
}: PortOneCheckoutProps) => {
  const [error, setError] = useState<string>("");

  // 갤럭시아머니트리 주문번호 40자 제한 대응: bookingId 앞 8자 + 타임스탬프
  const shortId = bookingId.replace(/-/g, "").slice(0, 8);
  const paymentId = `TP_${shortId}_${Date.now()}`;

  useEffect(() => {
    (async () => {
      try {
        if (!STORE_ID) {
          throw new Error("포트원 Store ID가 설정되지 않았습니다.");
        }
        if (!CHANNEL_KEY_CARD) {
          throw new Error("신용카드(갤럭시아머니트리) 채널키가 설정되지 않았습니다.");
        }

        const response = await PortOne.requestPayment({
          storeId: STORE_ID,
          channelKey: CHANNEL_KEY_CARD,
          paymentId,
          orderName,
          totalAmount: amount,
          currency: "CURRENCY_KRW",
          payMethod: "CARD",
          customer: {
            customerId: shortId,
            fullName: customerName,
            email: customerEmail || undefined,
          },
        });

        if (response?.code) {
          // 사용자 취소
          if (response.code === "FAILURE_TYPE_PG" && response.message?.includes("취소")) {
            onClose();
            return;
          }
          throw new Error(response.message || "결제에 실패했습니다.");
        }

        // 결제 성공 → success 페이지로 이동
        window.location.href = `${window.location.origin}/payment/success?paymentId=${paymentId}&bookingId=${bookingId}&amount=${amount}`;
      } catch (e: any) {
        console.error("[PortOneCheckout] Error:", e);
        if (e?.message?.includes("cancel") || e?.message?.includes("취소")) {
          onClose();
          return;
        }
        setError(e?.message || "결제 요청 중 오류가 발생했습니다.");
      }
    })();
  }, []);

  if (error) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center" onClick={onClose}>
        <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
          <div className="text-center">
            <div className="text-4xl mb-3">❌</div>
            <h3 className="text-lg font-bold text-achromatic-900 mb-2">결제 오류</h3>
            <p className="text-sm text-achromatic-500 mb-4">{error}</p>
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl text-sm font-bold text-white bg-primary-500 hover:bg-primary-600 transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center">
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="text-4xl mb-3 animate-pulse">💳</div>
        <p className="text-sm text-achromatic-500">결제창을 여는 중...</p>
      </div>
    </div>
  );
};

export default PortOneCheckout;
